/**
 * PURPOSE: Celdas y navegación por teclado de las grillas de captura de Atletismo.
 * RESPONSIBILITIES: `useGridNavigation` (Enter / Flecha abajo pasan a la misma columna de la fila
 *   siguiente; Flecha arriba a la anterior), `CellInput` con estado inválido, `NumberCell` para
 *   carril/orden, `ParticipantCell`, `InstitutionCell`, las acciones de fila (íconos: mover, reemplazar, quitar) y las columnas fijas
 *   (`buildFrozenColumns` + `FrozenScroll`).
 * COLLABORATORS: TrackGrid.jsx, RelayGrid.jsx, FieldGrid.jsx, VerticalGrid.jsx.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/components/grids/gridCells.jsx
 */
import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { FiRepeat, FiShuffle, FiTrash2 } from "react-icons/fi";

import { institutionAbrev, participantName } from "../../labels.js";
import { ConfirmPopover, IconAction } from "../IconAction.jsx";

export const useGridNavigation = () => {
  const refs = useRef(new Map());

  const register = useCallback(
    (row, col) => (element) => {
      const key = `${row}|${col}`;
      if (element) refs.current.set(key, element);
      else refs.current.delete(key);
    },
    []
  );

  const focusAt = useCallback((row, col) => {
    const element = refs.current.get(`${row}|${col}`);
    if (!element || element.disabled) return false;
    element.focus();
    if (typeof element.select === "function") element.select();
    return true;
  }, []);

  const move = useCallback(
    (row, col, delta) => {
      const rows = Array.from(refs.current.keys())
        .filter((key) => key.endsWith(`|${col}`))
        .map((key) => Number(key.split("|")[0]))
        .sort((a, b) => a - b);
      const candidates = delta > 0 ? rows.filter((r) => r > row) : rows.filter((r) => r < row).reverse();
      return candidates.some((candidate) => focusAt(candidate, col));
    },
    [focusAt]
  );

  const onKeyDown = useCallback(
    (row, col) => (event) => {
      if (event.key === "Enter" || event.key === "ArrowDown") {
        event.preventDefault();
        if (!move(row, col, 1) && event.key === "Enter") event.currentTarget.blur();
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        move(row, col, -1);
      }
    },
    [move]
  );

  return { register, onKeyDown };
};

/**
 * `invalid`: el servidor rechazó esta celda (siempre en rojo). `softInvalid`: la forma local no es
 * válida; se pinta en rojo solo cuando la celda no tiene el foco, para no parpadear mientras se teclea.
 */
export const CellInput = ({
  value,
  onChange,
  onBlur,
  invalid = false,
  softInvalid = false,
  disabled = false,
  inputRef,
  onKeyDown,
  placeholder,
  title,
  ariaLabel,
  className = "w-24",
  inputMode = "decimal",
  size = "md",
}) => (
  <input
    ref={inputRef}
    type="text"
    value={value ?? ""}
    onChange={(event) => onChange(event.target.value)}
    onBlur={onBlur}
    onKeyDown={onKeyDown}
    disabled={disabled}
    placeholder={placeholder}
    title={title}
    aria-label={ariaLabel}
    aria-invalid={invalid || softInvalid || undefined}
    inputMode={inputMode}
    autoComplete="off"
    spellCheck={false}
    className={`${className} rounded-md border bg-white text-center font-mono outline-none transition focus:ring-2 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 ${
      size === "sm" ? "px-1 py-0.5 text-[11px]" : "px-2 py-1 text-sm font-semibold"
    } ${
      invalid
        ? "border-rose-400 bg-rose-50 text-rose-700 focus:border-rose-500 focus:ring-rose-100"
        : softInvalid
        ? "border-slate-300 text-slate-800 focus:border-indigo-400 focus:ring-indigo-100 [&:not(:focus)]:border-rose-400 [&:not(:focus)]:bg-rose-50 [&:not(:focus)]:text-rose-700"
        : "border-slate-300 text-slate-800 focus:border-indigo-400 focus:ring-indigo-100"
    }`}
  />
);

/** Solo dígitos (carril / orden). */
export const NumberCell = ({ value, onChange, disabled, ariaLabel }) => (
  <CellInput
    value={value}
    onChange={(raw) => onChange(String(raw).replace(/\D/g, "").slice(0, 3))}
    disabled={disabled}
    ariaLabel={ariaLabel}
    inputMode="numeric"
    className="w-12"
    size="md"
  />
);

export const ParticipantCell = ({ participant }) => (
  <span className="block min-w-[10rem] text-sm font-semibold leading-5 text-slate-800 [overflow-wrap:anywhere]">
    {participantName(participant)}
  </span>
);

export const InstitutionCell = ({ institution }) => (
  <span className="text-xs font-bold text-slate-600" title={institution?.name || undefined}>
    {institutionAbrev(institution)}
  </span>
);

/**
 * Celda de acciones de una fila, como íconos chicos: "Mover a otra serie" (si la prueba tiene otra serie del mismo tipo),
 * "Reemplazar" (pista con carril: otro participante toma su carril) y "Quitar de la serie" (popover de confirmación; con
 * marca o estado avisa que se pierde y manda `confirmResult`). `lockReason` bloquea todo (serie terminada, acción en curso);
 * `removeLockReason` solo quitar y reemplazar (combinadas con resultado). El motivo va en el tooltip.
 */
export const RowActions = ({
  name,
  roundLabel = "la serie",
  lockReason = null,
  removeLockReason = null,
  hasResult = false,
  resultText = "",
  onMove,
  onReplace,
  onRemove,
}) => {
  const removeBlock = lockReason || removeLockReason;
  return (
    <div className="flex items-center justify-center gap-0.5">
      {onMove ? <IconAction icon={FiShuffle} label={`Mover a ${name} a otra serie`} disabledReason={lockReason} onClick={onMove} /> : null}
      {onReplace ? <IconAction icon={FiRepeat} label={`Reemplazar a ${name}`} disabledReason={removeBlock} onClick={onReplace} /> : null}
      <ConfirmPopover
        disabled={Boolean(removeBlock)}
        title={`¿Quitar a ${name} de ${roundLabel}?`}
        message={
          hasResult
            ? `Tiene ${resultText || "resultado"}: se pierde en esta serie. Vuelve a "inscritos sin serie".`
            : `Vuelve a "inscritos sin serie" y se puede reagregar.`
        }
        confirmLabel="Quitar"
        onConfirm={() => onRemove({ confirmResult: hasResult })}
        trigger={(props) => <IconAction {...props} icon={FiTrash2} tone="rose" label={`Quitar a ${name} de la serie`} disabledReason={removeBlock} />}
      />
    </div>
  );
};

export const thClass =
  "whitespace-nowrap px-2 py-2 text-left text-[11px] font-bold uppercase tracking-wide text-slate-500";
export const thCenterClass =
  "whitespace-nowrap px-2 py-2 text-center text-[11px] font-bold uppercase tracking-wide text-slate-500";
export const tdClass = "px-2 py-1.5 align-middle";

/**
 * Columnas fijas a la izquierda al desplazar la tabla en horizontal (carril, orden, atleta, universidad...).
 * `columns`: pares [clave, ancho en rem] en orden (los `null` se ignoran); cada columna queda pegada a la suma de las
 * anteriores. La última lleva el borde y `data-frozen-edge`, que `FrozenScroll` usa para medir dónde empieza la parte
 * desplazable. El fondo se hereda de la fila (`rowToneClass` o el `bg-*` del <tr>), que por eso siempre debe ser opaco.
 * Devuelve `frozen(clave, clases)` → props de la celda (<th>/<td>).
 */
export const buildFrozenColumns = (columns) => {
  const list = columns.filter(Boolean);
  const byKey = new Map();
  let left = 0;
  list.forEach(([key, width], index) => {
    byKey.set(key, { left, width, edge: index === list.length - 1 });
    left += width;
  });
  return (key, className = "") => {
    const column = byKey.get(key);
    if (!column) return { className };
    const width = `${column.width}rem`;
    return {
      className: `${className} sticky z-[1] bg-inherit${column.edge ? " shadow-[inset_-1px_0_0_0_#e2e8f0]" : ""}`,
      style: { left: `${column.left}rem`, width, minWidth: width, maxWidth: width },
      ...(column.edge ? { "data-frozen-edge": "" } : {}),
    };
  };
};

/**
 * Contenedor con scroll horizontal cuya barra solo cubre la parte desplazable (a la derecha de las columnas fijas).
 * La barra nativa se oculta y la reemplaza una barra espejo sincronizada, pegada al pie de la pantalla mientras la tabla
 * está a la vista. La rueda, el trackpad y el teclado siguen desplazando la tabla.
 */
export const FrozenScroll = ({ children }) => {
  const mainRef = useRef(null);
  const barRef = useRef(null);
  const [box, setBox] = useState({ frozen: 0, scroll: 0, client: 0 });

  useLayoutEffect(() => {
    const main = mainRef.current;
    if (!main) return undefined;
    const measure = () => {
      const edge = main.querySelector("[data-frozen-edge]");
      const frozen = edge ? Math.max(0, Math.round(edge.getBoundingClientRect().right - main.getBoundingClientRect().left)) : 0;
      const next = { frozen, scroll: main.scrollWidth, client: main.clientWidth };
      setBox((current) =>
        current.frozen === next.frozen && current.scroll === next.scroll && current.client === next.client ? current : next
      );
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(main);
    Array.from(main.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, []);

  const follow = (from, to) => () => {
    if (from.current && to.current && to.current.scrollLeft !== from.current.scrollLeft) {
      to.current.scrollLeft = from.current.scrollLeft;
    }
  };
  const overflow = box.scroll - box.client > 1;
  // En una ventana muy angosta la barra conserva al menos 120 px.
  const barOffset = Math.min(box.frozen, Math.max(0, box.client - 120));

  return (
    <div>
      <div
        ref={mainRef}
        onScroll={follow(mainRef, barRef)}
        className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {children}
      </div>
      {overflow ? (
        <div
          ref={barRef}
          onScroll={follow(barRef, mainRef)}
          className="sticky bottom-0 z-[2] overflow-x-auto bg-white"
          style={{ marginLeft: barOffset }}
          aria-hidden="true"
        >
          <div style={{ width: box.scroll - barOffset, height: 1 }} />
        </div>
      ) : null}
    </div>
  );
};

/** Fondo de fila (opaco, lo heredan las columnas fijas): error del servidor > cambiada sin guardar > normal. */
export const rowToneClass = ({ serverInvalid, changed, muted }) => {
  if (serverInvalid) return "bg-rose-50";
  if (changed) return "bg-amber-50";
  if (muted) return "bg-slate-50 text-slate-400";
  return "bg-white";
};

export const PlaceCell = ({ place }) => (
  <span className="text-sm font-black text-slate-700">{place ?? "—"}</span>
);
